import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminConfigLockedField,
  AdminSupportingText,
  AdminHeaderAction,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
  adminFormTwoColGridClass,
  AdminFormDropdownChevron,
  AdminCompactFormPage,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import {
  ADDITIONAL_DETAILS_OPTIONS,
  FLAG_ROUTE,
  FLAG_SOURCE_OPTIONS,
  SEVERITY_OPTIONS,
  applyDerivedFlagFields,
  buildFlagScopeKey,
  buildSampleFlagRules,
  createEmptyCondition,
  createEmptyFlagForm,
  formatConditionValueDisplay,
  formatConditionsPreview,
  formatFlagDateTime,
  getAdditionalDetailsLabel,
  getAvailableConditionFields,
  getCaseModuleLabel,
  getConditionFieldLabel,
  getConditionValueLabel,
  getConditionValueOptions,
  getContextCaptureText,
  getEligibilityText,
  getEvidenceRequirementText,
  getFlagModuleLabel,
  getFlagSourceLabel,
  getModulesForFlagSource,
  getOperatorsForField,
  getPermittedCaseModule,
  getSeverityLabel,
  getTargetTypeLabel,
  getTargetTypesForScope,
  isAdditionalDetailsLockedToRequired,
  isConditionValueAvailable,
  nextFlagRuleId,
  normalizeFlagForm,
  operatorIsMulti,
} from '../../../data/flagConfig';
import {
  canAddFlagRule,
  canEditFlagRule,
  canSaveFlagRule,
  canViewFlagConfiguration,
  getEditRuleSource,
  getPublishedRule,
  saveFlagRule,
} from '../../../data/flagRuleWorkflow';
import { matchesUrlName } from '../../../utils/slugify';

function emptyForm() {
  return {
    ...createEmptyFlagForm(),
    version: 1,
    pendingDraft: null,
    configurationHistory: [],
  };
}

function mergeIncomingRule(raw, ruleIdParam) {
  const normalized = normalizeFlagForm(raw || {});
  return {
    ...emptyForm(),
    ...normalized,
    id: normalized.id ?? ruleIdParam ?? '',
    version: raw?.version ?? 1,
    pendingDraft: raw?.pendingDraft ?? null,
    configurationHistory: raw?.configurationHistory ?? [],
  };
}

const TextValue = ({ label, value, mono = false, hint = null }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {mono ? (
      <p className="text-offblack text-sm py-2 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="py-2 break-words whitespace-pre-wrap">{value || '—'}</AdminDetailFieldValue>
    )}
    {hint ? <AdminSupportingText className="text-mediumgray">{hint}</AdminSupportingText> : null}
  </div>
);

const MultiChecklist = ({ label, options, values, onChange, disabled, error, required = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>
      {label} {required ? <span className="text-alertred">*</span> : null}
    </AdminDetailFieldLabel>
    <div className="space-y-2">
      {disabled || !options.length ? (
        <AdminDetailFieldValue className="py-2">—</AdminDetailFieldValue>
      ) : (
        options.map((option) => (
          <label key={option.value} className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 mt-0.5 text-primaryblue shrink-0"
              checked={(values || []).includes(option.value)}
              onChange={(e) => {
                const next = new Set(values || []);
                if (e.target.checked) next.add(option.value);
                else next.delete(option.value);
                onChange([...next]);
              }}
            />
            <span className="text-offblack text-sm break-words">{option.label}</span>
          </label>
        ))
      )}
    </div>
    {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
  </div>
);

const withCurrentOption = (options, value, getLabel) => {
  if (!value || options.some((option) => option.value === value)) return options;
  const label = getLabel(value) || value;
  return [...options, { value, label: `${label} (unavailable)` }];
};

const withCurrentValues = (options, values, field, form, condition) => {
  const next = [...options];
  (values || []).forEach((value) => {
    if (!value || next.some((option) => option.value === value)) return;
    next.push({
      value,
      label: formatConditionValueDisplay(field, value, form, condition),
    });
  });
  return next;
};

const FlagRuleDetail = () => {
  const navigate = useNavigate();
  const { ruleName } = useParams();
  const location = useLocation();
  const { auth } = useAuth();
  const isCreate = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');
  const isView = !isCreate && !isEdit;

  const canView = canViewFlagConfiguration(auth);
  const canAdd = canAddFlagRule(auth);
  const canEdit = canEditFlagRule(auth);
  const canSave = canSaveFlagRule(auth);

  const ruleFromState = location.state?.rule;
  const existingRulesFromState = location.state?.existingRules;

  const allRules = useMemo(
    () => existingRulesFromState ?? buildSampleFlagRules(),
    [existingRulesFromState],
  );

  const publishedBaseline = useMemo(() => {
    if (isCreate) return null;
    if (ruleFromState?.id) {
      const fromList = allRules.find((rule) => rule.id === ruleFromState.id);
      return getPublishedRule(fromList || ruleFromState);
    }
    if (ruleName) {
      const found = allRules.find((rule) => matchesUrlName(rule.ruleName, ruleName));
      return getPublishedRule(found || null);
    }
    return null;
  }, [isCreate, ruleFromState, ruleName, allRules]);

  const resolvedSource = useMemo(() => {
    if (ruleFromState && isView) {
      const foundRecord = allRules.find((rule) => rule.id === ruleFromState.id) || ruleFromState;
      return mergeIncomingRule(getPublishedRule(foundRecord) || foundRecord, foundRecord.id);
    }
    if (ruleFromState) {
      const foundRecord = allRules.find((rule) => rule.id === ruleFromState.id) || ruleFromState;
      const source = isEdit ? getEditRuleSource(foundRecord) : getPublishedRule(foundRecord) || foundRecord;
      return mergeIncomingRule(source, foundRecord.id);
    }
    if (!isCreate && ruleName) {
      const found = allRules.find((rule) => matchesUrlName(rule.ruleName, ruleName));
      if (found) {
        const source = isEdit ? getEditRuleSource(found) : getPublishedRule(found);
        return mergeIncomingRule(source, found.id);
      }
    }
    return emptyForm();
  }, [ruleFromState, isCreate, isEdit, isView, ruleName, allRules]);

  const [formData, setFormData] = useState(resolvedSource);
  const [errors, setErrors] = useState({});

  const scopeLocked = Boolean(!isCreate && formData.id);
  const moduleOptions = useMemo(
    () => withCurrentOption(getModulesForFlagSource(formData.flagSource), formData.module, getFlagModuleLabel),
    [formData.flagSource, formData.module],
  );
  const targetTypeOptions = useMemo(
    () => withCurrentOption(
      getTargetTypesForScope(formData.flagSource, formData.module),
      formData.targetType,
      getTargetTypeLabel,
    ),
    [formData.flagSource, formData.module, formData.targetType],
  );
  const caseModuleLabel = getCaseModuleLabel(getPermittedCaseModule(formData.flagSource, formData.targetType) || formData.caseModule);
  const detailsLocked = isAdditionalDetailsLockedToRequired(formData);
  const conditionFields = useMemo(() => getAvailableConditionFields(formData), [formData]);
  const isUserFlag = formData.flagSource === 'user_flag';
  useEffect(() => {
    setFormData(resolvedSource);
  }, [resolvedSource]);

  const clearFieldErrors = (field) => {
    setErrors((prev) => {
      const nextErr = {
        ...prev,
        [field]: undefined,
        activation: undefined,
        duplicate: undefined,
        overlap: undefined,
        conflict: undefined,
        concurrent: undefined,
        fixedScope: undefined,
        sourceDefinition: undefined,
        conditions: undefined,
        permission: undefined,
        checksUnavailable: undefined,
      };
      if (field === 'conditions') {
        Object.keys(nextErr).forEach((key) => {
          if (key.startsWith('condition_')) delete nextErr[key];
        });
      }
      return nextErr;
    });
  };

  const handleChange = (field, value) => {
    clearFieldErrors(field);
    setFormData((prev) => {
      let next = { ...prev, [field]: value };
      if (field === 'flagSource') {
        next.module = '';
        next.targetType = '';
        next.caseModule = '';
        next.additionalDetails = value === 'user_flag' ? next.additionalDetails : '';
        next.conditions = [];
      }
      if (field === 'module') {
        next.targetType = '';
        next.caseModule = '';
        next.conditions = [];
      }
      if (field === 'targetType') {
        next.conditions = [];
      }
      next = applyDerivedFlagFields(next);
      if (isAdditionalDetailsLockedToRequired(next)) {
        next.additionalDetails = 'required';
      }
      return next;
    });
  };

  const updateCondition = (index, patch) => {
    setErrors((prev) => ({ ...prev, [`condition_${index}`]: undefined, conditions: undefined, activation: undefined }));
    setFormData((prev) => {
      const conditions = [...(prev.conditions || [])];
      conditions[index] = { ...conditions[index], ...patch };
      if (patch.field) {
        conditions[index].values = [];
        conditions[index].operator = '';
        conditions[index].valueLabels = {};
      }
      if (patch.operator && !operatorIsMulti(patch.operator)) {
        conditions[index].values = (conditions[index].values || []).slice(0, 1);
      }
      if (patch.values) {
        const labels = { ...(conditions[index].valueLabels || {}) };
        patch.values.forEach((value) => {
          labels[value] = getConditionValueLabel(conditions[index].field, value, prev, conditions[index]) || value;
        });
        conditions[index].valueLabels = labels;
      }
      let next = { ...prev, conditions };
      if (isAdditionalDetailsLockedToRequired(next)) {
        next = { ...next, additionalDetails: 'required' };
      }
      return next;
    });
  };

  const buildRecord = (nowIso, userLabel) => {
    const derived = applyDerivedFlagFields(formData);
    const id = formData.id || nextFlagRuleId(allRules);
    const isNew = !formData.id;
    const additionalDetails = isAdditionalDetailsLockedToRequired(derived) ? 'required' : derived.additionalDetails;
    return {
      ...derived,
      id,
      additionalDetails: derived.flagSource === 'user_flag' ? additionalDetails : '',
      ruleName: derived.ruleName.trim(),
      description: derived.description.trim(),
      changeReason: derived.changeReason.trim(),
      version: formData.version || publishedBaseline?.version || 1,
      pendingDraft: formData.pendingDraft ?? null,
      configurationHistory: [
        ...(formData.configurationHistory || []),
        {
          action: isNew ? 'Add' : 'Edit',
          field: 'Rule',
          previousValue: isNew ? '—' : publishedBaseline?.ruleName || '—',
          newValue: derived.ruleName.trim(),
          status: 'saved',
          actor: userLabel,
          timestamp: nowIso,
          changeReason: derived.changeReason.trim(),
        },
      ],
      dateAdded: isNew ? nowIso : formData.dateAdded || nowIso,
      addedBy: isNew ? userLabel : formData.addedBy || userLabel,
      lastUpdated: nowIso,
      lastUpdatedBy: userLabel,
    };
  };

  const handleSave = () => {
    const nowIso = new Date().toISOString();
    const userLabel = 'Olivia Brown';
    setErrors((prev) => ({ ...prev, activation: undefined }));

    if ((isCreate && !canAdd) || (isEdit && !canEdit) || !canSave) {
      setErrors({ permission: 'You do not have permission to save this Rule.', activation: 'You do not have permission to save this Rule.' });
      return;
    }

    const record = buildRecord(nowIso, userLabel);
    let result;
    try {
      result = saveFlagRule(allRules, record, publishedBaseline || {}, {
        originalLastUpdated: resolvedSource.lastUpdated,
        originalScopeKey: publishedBaseline ? buildFlagScopeKey(publishedBaseline) : undefined,
        canSave: true,
        checksAvailable: true,
      });
    } catch {
      setErrors({
        checksUnavailable: 'Required checks could not be completed. Try again.',
        activation: 'Required checks could not be completed. Try again.',
      });
      return;
    }

    if (!result.success) {
      const next = { ...result.errors };
      if (result.summary.length) next.activation = result.summary.join(' ');
      setErrors(next);
      return;
    }

    navigate(FLAG_ROUTE, { state: { rules: result.rules } });
  };

  const renderSelect = (name, label, options, placeholder, disabledExtra = false, required = true) => {
    if (isView) {
      const selected = options.find((option) => option.value === formData[name]);
      return <TextValue label={label} value={selected?.label || formData[name] || '—'} />;
    }
    const emptyCompatible = !disabledExtra && !options.length;
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required && <span className="text-alertred">*</span>}
        </AdminDetailFieldLabel>
        <div className="relative w-full min-w-0">
          <select
            id={`flag-${name}-select`}
            value={formData[name] || ''}
            title={options.find((option) => option.value === formData[name])?.label || placeholder}
            onChange={(e) => handleChange(name, e.target.value)}
            disabled={disabledExtra || emptyCompatible}
            className={adminFormDetailSelectClasses(disabledExtra || emptyCompatible, Boolean(errors[name]))}
          >
            <option value="">{emptyCompatible ? 'No compatible options available' : placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <AdminFormDropdownChevron />
        </div>
        {errors[name] && <AdminFormFieldError>{errors[name]}</AdminFormFieldError>}
      </div>
    );
  };

  const renderTextarea = (name, label, placeholder, required = false) => {
    if (isView) return <TextValue label={label} value={formData[name]} />;
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required ? <span className="text-alertred">*</span> : null}
        </AdminDetailFieldLabel>
        <textarea
          id={`flag-${name}`}
          placeholder={placeholder}
          value={formData[name] || ''}
          onChange={(e) => handleChange(name, e.target.value)}
          className={adminFormDetailTextareaClasses(false, Boolean(errors[name]))}
          rows={3}
        />
        {errors[name] ? <AdminFormFieldError>{errors[name]}</AdminFormFieldError> : null}
      </div>
    );
  };

  const conditionBlock = (
    <div className="space-y-3">
      {errors.conditions ? <AdminFormFieldError>{errors.conditions}</AdminFormFieldError> : null}
      {(formData.conditions || []).length ? (
        <div className="space-y-3">
          {(formData.conditions || []).map((condition, index) => {
            const liveOptions = getConditionValueOptions(condition.field, formData);
            const valueOptions = withCurrentValues(liveOptions, condition.values, condition.field, formData, condition);
            const operatorOptions = getOperatorsForField(condition.field);
            const multi = operatorIsMulti(condition.operator);
            const hasUnavailable = (condition.values || []).some(
              (value) => !isConditionValueAvailable(condition.field, value, formData),
            );
            return (
              <div key={condition.id || index} className="rounded-xl border border-lightgray p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <AdminSupportingText className="mb-0 font-semibold !text-offblack">
                    Condition
                  </AdminSupportingText>
                  <button
                    type="button"
                    onClick={() => handleChange('conditions', (formData.conditions || []).filter((_, conditionIndex) => conditionIndex !== index))}
                    className="shrink-0 inline-flex items-center justify-center leading-none text-offblack hover:text-alertred"
                    aria-label="Remove Condition"
                  >
                    <i className="fi fi-rs-trash text-sm" aria-hidden />
                  </button>
                </div>
                {errors[`condition_${index}`] ? <AdminFormFieldError>{errors[`condition_${index}`]}</AdminFormFieldError> : null}
                {hasUnavailable ? (
                  <AdminSupportingText className="text-mediumgray">
                    One or more selected values are unavailable. Correct or remove them before Save.
                  </AdminSupportingText>
                ) : null}
                <div className="space-y-1 w-full min-w-0">
                  <AdminDetailFieldLabel>Field <span className="text-alertred">*</span></AdminDetailFieldLabel>
                  <div className="relative w-full min-w-0">
                    <select
                      value={condition.field || ''}
                      onChange={(e) => updateCondition(index, { field: e.target.value })}
                      className={adminFormDetailSelectClasses(false, Boolean(errors[`condition_${index}`]))}
                    >
                      <option value="">Select field</option>
                      {withCurrentOption(conditionFields, condition.field, getConditionFieldLabel).map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <AdminFormDropdownChevron />
                  </div>
                </div>
                <div className="space-y-1 w-full min-w-0">
                  <AdminDetailFieldLabel>Operator <span className="text-alertred">*</span></AdminDetailFieldLabel>
                  <div className="relative w-full min-w-0">
                    <select
                      value={condition.operator || ''}
                      onChange={(e) => updateCondition(index, { operator: e.target.value })}
                      disabled={!condition.field}
                      className={adminFormDetailSelectClasses(!condition.field)}
                    >
                      <option value="">Select operator</option>
                      {operatorOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <AdminFormDropdownChevron />
                  </div>
                </div>
                {multi ? (
                  <MultiChecklist
                    label="Value"
                    options={valueOptions}
                    values={condition.values}
                    onChange={(value) => updateCondition(index, { values: value })}
                    disabled={!condition.field || !condition.operator}
                    required
                  />
                ) : (
                  <div className="space-y-1 w-full min-w-0">
                    <AdminDetailFieldLabel>Value <span className="text-alertred">*</span></AdminDetailFieldLabel>
                    <div className="relative w-full min-w-0">
                      <select
                        value={(condition.values || [])[0] || ''}
                        onChange={(e) => updateCondition(index, { values: e.target.value ? [e.target.value] : [] })}
                        disabled={!condition.field || !condition.operator}
                        className={adminFormDetailSelectClasses(!condition.field || !condition.operator)}
                      >
                        <option value="">Select value</option>
                        {valueOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <AdminFormDropdownChevron />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
      {conditionFields.length ? (
        <button
          type="button"
          onClick={() => handleChange('conditions', [
            ...(formData.conditions || []),
            createEmptyCondition(),
          ])}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primaryblue hover:text-brightblue"
        >
          <i className="fi fi-rs-plus text-xs" aria-hidden />
          Add Condition
        </button>
      ) : null}
    </div>
  );

  const additionalDetailsDisplay = isUserFlag
    ? (detailsLocked ? 'Required' : getAdditionalDetailsLabel(formData.additionalDetails) || '—')
    : '—';

  const hasValidationErrors = Boolean(
    errors.activation
    || errors.duplicate
    || errors.overlap
    || errors.conflict
    || errors.concurrent
    || errors.sourceDefinition
    || errors.permission
    || errors.checksUnavailable,
  );

  if (!canView || (isCreate && !canAdd) || (isEdit && !canEdit)) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Flag</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray">
          You do not have permission to {isView ? 'view' : isCreate ? 'add' : 'edit'} this Rule.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  if (isView) {
    const conditionsPreview = formatConditionsPreview(formData.conditions, formData);
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>
          Flag Rule Detail
        </AdminPageTitle>

        <div className={`space-y-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className={`${adminFormTwoColGridClass} gap-y-4`}>
              <TextValue label="Rule ID" value={formData.id} mono />
              <TextValue label="Rule Name" value={formData.ruleName} />
              <div className="sm:col-span-2">
                <TextValue label="Internal Description" value={formData.description} />
              </div>
              <TextValue label="Flag Source" value={getFlagSourceLabel(formData.flagSource)} />
              <TextValue label="Module" value={getFlagModuleLabel(formData.module)} />
              <TextValue label="Target Type" value={getTargetTypeLabel(formData.targetType)} />
              <TextValue label="Severity" value={getSeverityLabel(formData.severity)} />
              <TextValue label="Case Module" value={getCaseModuleLabel(formData.caseModule)} />
              {isUserFlag ? (
                <TextValue label="Additional Details" value={additionalDetailsDisplay} />
              ) : null}
              <div className="sm:col-span-2">
                <TextValue label="Eligibility" value={getEligibilityText(formData.flagSource, formData.targetType)} />
              </div>
              <div className="sm:col-span-2">
                <TextValue label="Evidence Requirement" value={getEvidenceRequirementText(formData.flagSource, formData.targetType)} />
              </div>
              <div className="sm:col-span-2">
                <TextValue label="Context Capture Requirement" value={getContextCaptureText(formData.targetType)} />
              </div>
              <div className="sm:col-span-2">
                <TextValue
                  label="Conditions"
                  value={conditionsPreview.lines.length ? conditionsPreview.lines.join('\n') : 'No Conditions'}
                />
              </div>
              <TextValue label="Date Added" value={formatFlagDateTime(formData.dateAdded)} />
              <TextValue label="Added By" value={formData.addedBy} />
              <TextValue label="Last Updated" value={formatFlagDateTime(formData.lastUpdated)} />
              <TextValue label="Last Updated By" value={formData.lastUpdatedBy} />
            </div>
          </DashboardMainContainer>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={isCreate ? 'Add Flag Rule' : 'Edit Flag Rule'}
        onSubmit={handleSave}
        showSave={false}
      >
        {formData.id ? (
          <AdminConfigLockedField label="Rule ID" value={formData.id} mono isView={false} />
        ) : null}
        <div className="space-y-1 w-full min-w-0">
          <AdminDetailFieldLabel>
            Rule Name <span className="text-alertred">*</span>
          </AdminDetailFieldLabel>
          <input
            id="flag-rule-name"
            type="text"
            placeholder="Enter rule name"
            value={formData.ruleName || ''}
            onChange={(e) => handleChange('ruleName', e.target.value)}
            className={adminFormDetailInputClasses(false, Boolean(errors.ruleName))}
          />
          {errors.ruleName && <AdminFormFieldError>{errors.ruleName}</AdminFormFieldError>}
        </div>
        {renderTextarea('description', 'Internal Description', 'Enter an internal description', true)}
        {scopeLocked ? (
          <>
            <AdminConfigLockedField label="Flag Source" value={getFlagSourceLabel(formData.flagSource)} isView={false} />
            <AdminConfigLockedField label="Module" value={getFlagModuleLabel(formData.module)} isView={false} />
            <AdminConfigLockedField label="Target Type" value={getTargetTypeLabel(formData.targetType)} isView={false} />
          </>
        ) : (
          <>
            {renderSelect('flagSource', 'Flag Source', FLAG_SOURCE_OPTIONS, 'Select flag source')}
            {renderSelect(
              'module',
              'Module',
              moduleOptions,
              formData.flagSource ? 'Select module' : 'Select flag source first',
              !formData.flagSource,
            )}
            {renderSelect(
              'targetType',
              'Target Type',
              targetTypeOptions,
              formData.module ? 'Select target type' : 'Select module first',
              !formData.module,
            )}
          </>
        )}
        {renderSelect('severity', 'Severity', SEVERITY_OPTIONS, 'Select severity')}
        <AdminConfigLockedField
          label="Case Module"
          value={caseModuleLabel || '—'}
          isView={false}
        />
        {errors.caseModule ? <AdminFormFieldError>{errors.caseModule}</AdminFormFieldError> : null}
        {isUserFlag ? (
          detailsLocked ? (
            <AdminConfigLockedField label="Additional Details" value="Required" isView={false} />
          ) : (
            renderSelect('additionalDetails', 'Additional Details', ADDITIONAL_DETAILS_OPTIONS, 'Select requirement')
          )
        ) : null}
        <AdminConfigLockedField
          label="Eligibility"
          value={getEligibilityText(formData.flagSource, formData.targetType)}
          isView={false}
        />
        <AdminConfigLockedField
          label="Evidence Requirement"
          value={getEvidenceRequirementText(formData.flagSource, formData.targetType)}
          isView={false}
        />
        <AdminConfigLockedField
          label="Context Capture Requirement"
          value={getContextCaptureText(formData.targetType)}
          isView={false}
        />
        {errors.sourceDefinition ? <AdminFormFieldError>{errors.sourceDefinition}</AdminFormFieldError> : null}
        {errors.fixedScope ? <AdminFormFieldError>{errors.fixedScope}</AdminFormFieldError> : null}
        {conditionBlock}
        {renderTextarea('changeReason', 'Change Reason', 'Enter a change reason', true)}
        {hasValidationErrors ? (
          <div className="space-y-2">
            {errors.permission ? <AdminFormFieldError>{errors.permission}</AdminFormFieldError> : null}
            {errors.checksUnavailable ? <AdminFormFieldError>{errors.checksUnavailable}</AdminFormFieldError> : null}
            {errors.duplicate ? <AdminFormFieldError>{errors.duplicate}</AdminFormFieldError> : null}
            {errors.conflict ? <AdminFormFieldError>{errors.conflict}</AdminFormFieldError> : null}
            {errors.overlap ? <AdminFormFieldError>{errors.overlap}</AdminFormFieldError> : null}
            {errors.concurrent ? <AdminFormFieldError>{errors.concurrent}</AdminFormFieldError> : null}
            {errors.sourceDefinition ? <AdminFormFieldError>{errors.sourceDefinition}</AdminFormFieldError> : null}
            {errors.activation ? <AdminFormFieldError>{errors.activation}</AdminFormFieldError> : null}
          </div>
        ) : null}
        <div className="flex items-center justify-end">
          <AdminHeaderAction type="submit" disabled={!canSave}>Save</AdminHeaderAction>
        </div>
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default FlagRuleDetail;
